import KiccEasyCardApi from '@apis/easyCard/KiccEasyCardApi';
import cartQueries from '@apis/queries/cartQueries';
import couponQueries from '@apis/queries/couponQueries';
import discountQueries from '@apis/queries/discountQueries';
import giftBoxQueries from '@apis/queries/giftBoxQueries';
import kiccEasyCardQueries from '@apis/queries/kiccEasyCardQueries';
import orderLogQueries from '@apis/queries/orderLogQueries';
import orderQueries from '@apis/queries/orderQueries';
import pointQueries from '@apis/queries/pointQueries';
import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import userQueries from '@apis/queries/userQueries';
import useAuthenticatePin from '@hooks/native/useAuthenticatePin';
import useDeviceId from '@hooks/native/useDeviceId';
import useRegisterPin from '@hooks/native/useRegisterPin';
import useLoading from '@hooks/ui/useLoading';
import { KiccPaymentRequest } from '@models/easyCard/kicc';
import { ErrorResponse } from '@models/error';
import { useActivity, useFlow } from '@stackflow/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const MAX_LENGTH = 6;
/** 화면 진입 후 생체 인증을 자동으로 켜기까지의 지연 (ms) */
const AUTO_BIO_DELAY = 300;

interface Props {
    orderNo: string;
    cardId: string;
}

/** 결제 비밀번호 입력 상태와 간편카드 결제 요청 */
function useEasyCardPayment({ orderNo, cardId }: Props) {
    const { t } = useTranslation('order');
    const { t: tEasyCard } = useTranslation('easyCard');
    const { push, pop, replace } = useFlow();
    const queryClient = useQueryClient();
    const { startLoading, stopLoading } = useLoading();
    const [pin, setPin] = useState('');
    const deviceId = useDeviceId();
    const { data: easyCard } = useQuery({
        ...kiccEasyCardQueries.bioDevice(deviceId!),
        enabled: !!deviceId,
    });
    const isBioDevice = !!easyCard?.isBioDevice;
    const [useBio, setUseBio] = useState(false);
    // 진입 시 자동 생체 인증은 한 번만 요청한다
    // 다시 입력받을 때마다 키패드를 새로 섞는다
    const [attempt, setAttempt] = useState(0);

    const reset = () => {
        setPin('');
        setUseBio(false);
        setAttempt((prev) => prev + 1);
    };

    // 결제를 마친 뒤 주문 내역으로 이동한다
    const complete = () => {
        queryClient.invalidateQueries({ queryKey: orderQueries._def });
        queryClient.invalidateQueries({ queryKey: userQueries._def });
        queryClient.invalidateQueries({ queryKey: couponQueries._def });
        queryClient.invalidateQueries({ queryKey: orderLogQueries._def });
        queryClient.invalidateQueries({ queryKey: giftBoxQueries._def });
        queryClient.invalidateQueries({ queryKey: userPrepaidCardQueries._def });
        queryClient.invalidateQueries({ queryKey: pointQueries._def });
        queryClient.resetQueries({ queryKey: discountQueries._def });
        queryClient.resetQueries({ queryKey: cartQueries._def });
        // 무료 주문 완료와 같은 위치로 돌아가되, 이 화면이 하나 더 쌓여 있다
        pop(5, { animate: false });
        push('OrderLogsActivity', { duration: 'ALL' }, { animate: false });
        push('OrderLogActivity', { orderNo });
    };

    // 결제는 이미 끝났으므로 생체 인증 등록 결과와 상관없이 주문 내역으로 이동한다
    const { registerPin } = useRegisterPin({
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: kiccEasyCardQueries.bioDevice._def });
            complete();
        },
        onError(status) {
            toast.error(tEasyCard('pin.bio.registerError', { status }));
            complete();
        },
    });

    const { mutate, isPending } = useMutation({
        // 생체 인증 기기 등록까지 마쳤는지를 돌려준다. 결제는 이미 끝났으므로 등록 실패는 오류로 올리지 않는다
        async mutationFn({ registerBio, ...request }: KiccPaymentRequest & { registerBio: boolean }) {
            await KiccEasyCardApi.payment(request);
            if (!registerBio) {
                return true;
            }
            if (!deviceId) {
                return false;
            }
            try {
                await KiccEasyCardApi.setBioDevice({ pin: request.pin, deviceId });
                return true;
            } catch (e) {
                console.error(e);
                return false;
            }
        },
        onMutate() {
            startLoading();
        },
        onSuccess(isBioDeviceSet, { pin: value, registerBio }) {
            if (!registerBio) {
                complete();
                return;
            }
            if (!isBioDeviceSet) {
                toast.error(tEasyCard('pin.bioDevice.error'));
                complete();
                return;
            }
            registerPin(value);
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            const code = e.response?.data?.code;
            if (code === 'EASY_CARD_PIN_LOCKED') {
                // 잠긴 상태에서는 더 입력해도 결제할 수 없으므로 이 화면을 본인인증 안내 모달로 바꾼다
                replace('EasyCardPinLockedModal', {});
                return;
            }
            toast.error(t(code === 'EASY_CARD_PIN_MISMATCH'
                ? 'easyCardPin.mismatch' : 'easyCardPin.error'));
            reset();
        },
        onSettled() {
            stopLoading();
        },
    });

    /** registerBio면 결제 후 이 기기를 생체 인증 기기로 등록한다 */
    const pay = (value: string, registerBio: boolean) => mutate({
        orderNo, cardId, pin: value, registerBio,
    });

    const changePin = (next: string) => {
        if (isPending) {
            return;
        }
        setPin(next);
        if (next.length === MAX_LENGTH && next !== pin) {
            // 생체 인증을 체크한 채 직접 입력했다면 아직 등록되지 않은 상태다
            pay(next, useBio);
        }
    };

    const { authenticatePin } = useAuthenticatePin({
        onSuccess(value) {
            setPin(value);
            pay(value, false);
        },
        onNotRegister() {
            setUseBio(false);
        },
        onError(status) {
            toast.error(tEasyCard('pin.bio.authError', { status }));
            setUseBio(false);
        },
    });

    const handleUseBioChange = (value: boolean) => {
        if (isPending) {
            return;
        }
        setUseBio(value);
    };
    const { transitionState } = useActivity();

    useEffect(() => {
        if (isBioDevice && transitionState === 'enter-done') {
            const timeout = setTimeout(() => {
                setUseBio(true);
            }, 100);
            return () => clearTimeout(timeout);
        }
        return () => {};
    }, [isBioDevice, transitionState]);

    // 생체인증 기기가 아니면 인증 없이 체크만 해 두고, PIN 입력 후 결제하면서 등록한다
    useEffect(() => {
        if (useBio && isBioDevice) {
            authenticatePin();
        }
    }, [useBio]);

    return {
        pin,
        attempt,
        useBio,
        changePin,
        handleUseBioChange,
    };
}

export default useEasyCardPayment;
